<script setup lang="ts">
import { useRouter, useRoute } from 'vue-router';
import InflectionMark from '@/components/InflectionMark.vue';

const router = useRouter();
const route = useRoute();

function isActive(path: string) {
  return route.path === path || route.path.startsWith(path + '/');
}
</script>

<template>
  <nav class="fixed bottom-0 left-0 right-0 nav-surface bg-[#0b0b0f]/95 backdrop-blur-xl border-t border-white/5 z-40 safe-bottom">
    <div class="max-w-lg mx-auto flex items-stretch justify-around">

      <button
        @click="router.push('/')"
        class="flex-1 flex flex-col items-center gap-1 py-2 pt-2.5 transition-colors"
        :class="route.path === '/' ? 'text-white' : 'text-gray-600'"
      >
        <InflectionMark :size="30" :glow="false" />
        <span class="text-[13px]">Home</span>
      </button>

      <button
        data-tour="nav-new"
        @click="router.push('/analyze')"
        class="flex-1 flex flex-col items-center gap-1 py-2 pt-2.5 transition-colors"
        :class="isActive('/analyze') || isActive('/results') ? 'text-white' : 'text-gray-600'"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
        <span class="text-[13px]">New</span>
      </button>

      <button
        data-tour="nav-people"
        @click="router.push('/people')"
        class="flex-1 flex flex-col items-center gap-1 py-2 pt-2.5 transition-colors"
        :class="isActive('/people') ? 'text-white' : 'text-gray-600'"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span class="text-[13px]">People</span>
      </button>

      <button
        data-tour="nav-teams"
        @click="router.push('/teams')"
        class="flex-1 flex flex-col items-center gap-1 py-2 pt-2.5 transition-colors"
        :class="isActive('/teams') ? 'text-white' : 'text-gray-600'"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
        </svg>
        <span class="text-[13px]">Teams</span>
      </button>

      <button
        data-tour="nav-coach"
        @click="router.push('/coach')"
        class="flex-1 flex flex-col items-center gap-1 py-2 pt-2.5 transition-colors"
        :class="isActive('/coach') ? 'text-white' : 'text-gray-600'"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
        </svg>
        <span class="text-[13px]">Coach</span>
      </button>
    </div>
  </nav>
</template>

<style scoped>
.safe-bottom {
  padding-bottom: env(safe-area-inset-bottom, 0);
}
</style>
